import { inject, Injectable } from '@angular/core'

import { difference, intersection, pick, uniq } from 'lodash-es'
import { MessageService } from 'primeng/api'
import { Subject } from 'rxjs'

import { Group, GroupAssignments, GroupCounters } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { ErrorHandler } from '@ti/core/common/services'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { DriversStore } from '@ti/shared/drivers/services'
import { VehiclesStore } from '@ti/shared/fleet/services'

@Injectable({ providedIn: 'root' })
export class GroupsManager {
  public readonly updated$ = new Subject<string[] | void>()

  protected readonly api = inject(ApiService)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly errorHandler = inject(ErrorHandler)
  protected readonly languageService = inject(LanguageService)
  protected readonly messageService = inject(MessageService)
  protected readonly driversStore = inject(DriversStore)
  protected readonly vehiclesStore = inject(VehiclesStore)

  public async create(data: Pick<Group, 'name'>) {
    try {
      const result = await this.api.fleet.groups.create(pick(data, ['name']))
      this.updated$.next([result?.id])

      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.groups.created-message.detail',
        },
        { name: data.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      return result
    } catch (error) {
      await this.errorHandler.notifyUser(error, `Cannot create group`)
    }
  }

  public async update(id: string, data: Pick<Group, 'name'>) {
    try {
      const result = await this.api.fleet.groups.update(id, pick(data, ['name']))
      this.updated$.next([result?.id])

      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.groups.updated-message.detail',
        },
        { name: data.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      return result
    } catch (error) {
      await this.errorHandler.notifyUser(error, `Cannot update group`)
    }
  }

  public async delete(id: string, data: Pick<Group, 'name'>) {
    const dialogResult = await this.confirmDelete(data?.name || '')
    if (!dialogResult) return

    try {
      await this.api.fleet.groups.remove(id)
      this.updated$.next([id])

      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.groups.deleted-message.detail',
        },
        { name: data.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })
    } catch (error) {
      await this.errorHandler.notifyUser(error, `Cannot delete group`)
    }
  }

  public async bulkDelete(ids: string[]) {
    if (!ids.length) return

    const dialogResult = await this.confirmBulkDelete(ids.length)
    if (!dialogResult) return

    try {
      await this.api.fleet.groups.bulkRemove(ids)

      const labels = await this.languageService.massTranslate(
        {
          summary: 'fleet-settings.groups.bulk-deleted-message.summary',
          detail: 'fleet-settings.groups.bulk-deleted-message.detail',
        },
        { count: ids.length },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      this.updated$.next(ids)
      this.triggerRefreshForRelatedEntities(ids)
    } catch (error) {
      await this.errorHandler.notifyUser(error, 'Cannot bulk-delete groups')
    }
  }

  public async duplicate(id: string) {
    try {
      const result = await this.api.fleet.groups.duplicate(id)
      this.updated$.next([result.id])
      // Update entities assigned to the ORIGINAL group
      this.triggerRefreshForRelatedEntities([id])

      const labels = await this.languageService.massTranslate(
        {
          detail: 'fleet-settings.groups.duplicated-message.detail',
        },
        { name: result.name || '' },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      return result
    } catch (error) {
      await this.errorHandler.notifyUser(error, 'Cannot duplicate group')
    }
  }

  public async bulkDuplicate(ids: string[]) {
    try {
      const result = await this.api.fleet.groups.bulkDuplicate(ids)
      this.updated$.next(result.map((item) => item.id))
      this.triggerRefreshForRelatedEntities(ids)

      const labels = await this.languageService.massTranslate(
        {
          summary: 'fleet-settings.groups.bulk-duplicated-message.summary',
          detail: 'fleet-settings.groups.bulk-duplicated-message.detail',
        },
        { count: ids.length },
      )
      this.messageService.add({ ...labels, life: 3000, severity: 'success' })

      return result
    } catch (error) {
      await this.errorHandler.notifyUser(error, 'Cannot bulk-duplicate groups')
    }
  }

  public async assignToGroup(groupId: string, data: GroupAssignments) {
    try {
      await this.api.fleet.groups.assign(groupId, data)
    } catch (e) {
      await this.errorHandler.notifyUser(e, 'Cannot assign group to vehicles')
    }
  }

  public async unassignFromGroup(groupId: string, data: GroupAssignments) {
    try {
      await this.api.fleet.groups.unassign(groupId, data)
    } catch (e) {
      await this.errorHandler.notifyUser(e, 'Cannot unassign group to vehicles')
    }
  }

  public async updateAssignments(data: Group & GroupAssignments, prev: GroupAssignments) {
    const driverIdsToKeep = intersection(prev.driverIds, data.driverIds)
    const driverIdsToAssign = difference(data.driverIds, driverIdsToKeep)
    const driverIdsToRemove = difference(prev.driverIds, driverIdsToKeep)

    const vehicleIdsToKeep = intersection(prev.vehicleIds, data.vehicleIds)
    const vehicleIdsToAssign = difference(data.vehicleIds, vehicleIdsToKeep)
    const vehicleIdsToRemove = difference(prev.vehicleIds, vehicleIdsToKeep)

    if (driverIdsToAssign.length || vehicleIdsToAssign.length) {
      await this.assignToGroup(data.id, {
        driverIds: driverIdsToAssign,
        vehicleIds: vehicleIdsToAssign,
      })
    }
    if (driverIdsToRemove.length || vehicleIdsToRemove.length) {
      await this.unassignFromGroup(data.id, {
        driverIds: driverIdsToRemove,
        vehicleIds: vehicleIdsToRemove,
      })
    }

    let hasChanges = false
    if (driverIdsToAssign.length || driverIdsToRemove.length) {
      hasChanges = true
      this.driversStore.triggerRefresh(uniq([...driverIdsToAssign, ...driverIdsToRemove]))
    }
    if (vehicleIdsToAssign.length || vehicleIdsToRemove.length) {
      hasChanges = true
      this.vehiclesStore.triggerRefresh(uniq([...vehicleIdsToAssign, ...vehicleIdsToRemove]))
    }
    if (hasChanges) {
      this.updated$.next([data.id])
    }
  }

  // TODO: Implement cache for group counters
  public async getGroupCountersMap(groupIds: string[]) {
    const result = await this.api.fleet.groups.listCounters({ filter: { id: groupIds } })
    return new Map<string, GroupCounters>(result.map((data) => [data.id, data]))
  }

  protected async confirmDelete(name: string) {
    const labels = await this.languageService.massTranslate(
      {
        summary: 'fleet-settings.groups.delete-dialog.summary',
        description: 'fleet-settings.groups.delete-dialog.description',
        confirmButton: 'common.yes-delete',
        cancelButton: 'button.cancel',
      },
      { name },
    )
    return this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
  }

  protected async confirmBulkDelete(count: number) {
    const labels = await this.languageService.massTranslate(
      {
        summary: 'fleet-settings.groups.bulk-delete-dialog.summary',
        description: 'fleet-settings.groups.bulk-delete-dialog.description',
        confirmButton: 'common.yes-delete',
        cancelButton: 'button.cancel',
      },
      { count },
    )
    return this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
  }

  protected triggerRefreshForRelatedEntities(groupIds?: string[]) {
    if (!groupIds) {
      // Refresh all vehicles and drivers
      this.driversStore.triggerRefresh()
      this.vehiclesStore.triggerRefresh()
    } else {
      const driverToVehicleIdMap = this.driversStore.instantGroupDriverIdsMap()
      const driverIdsToRefresh = groupIds
        .map((groupId) => driverToVehicleIdMap.get(groupId) ?? [])
        .reduce((carry, item) => [...carry, ...item], [])

      this.driversStore.triggerRefresh(driverIdsToRefresh.length ? driverIdsToRefresh : undefined)

      const groupToVehicleIdsMap = this.vehiclesStore.instantGroupVehicleIdsMap()
      const vehicleIdsToRefresh = groupIds
        .map((groupId) => groupToVehicleIdsMap.get(groupId) ?? [])
        .reduce((carry, item) => [...carry, ...item], [])

      this.vehiclesStore.triggerRefresh(
        vehicleIdsToRefresh.length ? vehicleIdsToRefresh : undefined,
      )
    }
  }
}
